import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { 
  List, Plus, Lock, Users, Globe, Star, ChevronRight, 
  Trash2, Edit2, X, Loader2, User, LogIn
} from 'lucide-react';

function MyLists() {
  const { user } = useAuth();
  const { lists, bars, createList, deleteList, updateList, friendLists, users } = useApp();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [newList, setNewList] = useState({
    title: '',
    description: '',
    privacy: 'private'
  });

  const handleCreateList = async (e) => {
    e.preventDefault();
    if (!newList.title.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      await createList(newList.title.trim(), newList.description.trim(), newList.privacy);
      setNewList({ title: '', description: '', privacy: 'private' });
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateList = async (e) => {
    e.preventDefault();
    if (!editingList) return;
    
    setLoading(true);
    setError('');
    
    try {
      await updateList(editingList.id, {
        title: editingList.title,
        description: editingList.description,
        privacy: editingList.privacy
      });
      setEditingList(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteList = async (listId) => {
    if (!confirm('Are you sure you want to delete this list?')) return;
    
    try {
      await deleteList(listId);
    } catch (err) {
      setError(err.message);
    }
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'public': return <Globe className="w-4 h-4 text-green-400" />;
      case 'friends': return <Users className="w-4 h-4 text-blue-400" />;
      default: return <Lock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getListBars = (list) => {
    if (!list.list_bars) return [];
    return list.list_bars
      .sort((a, b) => a.position - b.position)
      .map(lb => bars.find(b => b.id === lb.bar_id))
      .filter(Boolean);
  };

  // Show signup prompt for non-logged-in users
  if (!user) {
    return (
      <div className="p-4 pb-24 max-w-2xl mx-auto">
        <div className="text-center py-16 bg-dive-dark rounded-xl border border-dive-gray">
          <List className="w-16 h-16 text-dive-neon mx-auto mb-4" />
          <h1 className="text-2xl font-display text-white mb-3">Create Your Bar Lists</h1>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Sign in to create custom lists of your favorite dive bars, track bars you want to try, and share lists with friends!
          </p>
          <Link
            to="/auth"
            className="btn-primary inline-flex items-center gap-2"
          >
            <LogIn className="w-5 h-5" />
            Sign In or Create Account
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-white flex items-center gap-2">
            <List className="w-6 h-6 text-dive-neon" />
            My Lists
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Organize your favorite dive bars
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New List
        </button>
      </div>

      {/* My Lists */}
      <div className="space-y-4">
        {lists.length === 0 ? (
          <div className="text-center py-12 bg-dive-dark rounded-xl border border-dive-gray">
            <List className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">No lists yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Your First List
            </button>
          </div>
        ) : (
          lists.map(list => {
            const listBars = getListBars(list);
            
            return (
              <Link
                key={list.id}
                to={`/lists/${list.id}`}
                className="block bg-dive-dark rounded-xl border border-dive-gray p-4 hover:border-dive-neon/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {list.is_system && (
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      )}
                      <h3 className="text-white font-semibold">{list.title}</h3>
                      {getPrivacyIcon(list.privacy)}
                    </div>
                    {list.description && (
                      <p className="text-gray-400 text-sm mb-2">{list.description}</p>
                    )}
                    <p className="text-gray-500 text-sm">
                      {listBars.length} {listBars.length === 1 ? 'bar' : 'bars'}
                    </p>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-500 ml-2" />
                </div>

                {/* Actions (don't navigate when clicking these) */}
                {!list.is_system && (
                  <div 
                    className="flex gap-2 mt-3 pt-3 border-t border-dive-gray"
                    onClick={(e) => e.preventDefault()}
                  >
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setEditingList(list);
                      }}
                      className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleDeleteList(list.id);
                      }}
                      className="text-gray-400 hover:text-red-400 text-sm flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </Link>
            );
          })
        )}
      </div>

      {/* Friends' Lists Section */}
      {friendLists && friendLists.length > 0 && (
        <div className="mt-10">
          <h2 className="text-xl font-display text-white flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-blue-400" />
            Friends' Lists
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Lists shared by your friends
          </p>
          <div className="space-y-4">
            {friendLists.map(list => {
              const listBars = getListBars(list);
              const listOwner = list.profiles;
              
              return (
                <Link
                  key={list.id}
                  to={`/lists/${list.id}`}
                  className="block bg-dive-dark rounded-xl border border-dive-gray p-4 hover:border-blue-500/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Owner info */}
                      <div className="flex items-center gap-2 mb-2">
                        {listOwner?.avatar_url ? (
                          <img
                            src={listOwner.avatar_url}
                            alt={listOwner.display_name || listOwner.username}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-dive-gray flex items-center justify-center">
                            <User className="w-3 h-3 text-gray-400" />
                          </div>
                        )}
                        <span className="text-gray-400 text-sm">
                          {listOwner?.display_name || listOwner?.username || 'Unknown'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-semibold">{list.title}</h3>
                        {getPrivacyIcon(list.privacy)}
                      </div>
                      {list.description && (
                        <p className="text-gray-400 text-sm mb-2">{list.description}</p>
                      )}
                      <p className="text-gray-500 text-sm">
                        {listBars.length} {listBars.length === 1 ? 'bar' : 'bars'}
                      </p>
                    </div>

                    <ChevronRight className="w-5 h-5 text-gray-500 ml-2" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Create List Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dive-dark border border-dive-gray rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display text-white">Create New List</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleCreateList} className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-1 block">List Name</label>
                <input
                  type="text"
                  value={newList.title}
                  onChange={(e) => setNewList(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Best Jukeboxes in Town"
                  className="w-full"
                  required
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-1 block">Description (optional)</label>
                <textarea
                  value={newList.description}
                  onChange={(e) => setNewList(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Bars with the best music selection..."
                  className="w-full h-20 resize-none"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Privacy</label>
                <div className="flex gap-2">
                  {[
                    { value: 'private', label: 'Private', icon: Lock },
                    { value: 'friends', label: 'Friends', icon: Users },
                    { value: 'public', label: 'Public', icon: Globe }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setNewList(prev => ({ ...prev, privacy: value }))}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm flex items-center justify-center gap-2 transition-colors ${
                        newList.privacy === value
                          ? 'border-dive-neon bg-dive-neon/20 text-white'
                          : 'border-dive-gray text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !newList.title.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Create List
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit List Modal */}
      {editingList && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dive-dark border border-dive-gray rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display text-white">Edit List</h2>
              <button
                onClick={() => setEditingList(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleUpdateList} className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-1 block">List Name</label>
                <input
                  type="text"
                  value={editingList.title}
                  onChange={(e) => setEditingList(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full"
                  required
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-1 block">Description</label>
                <textarea
                  value={editingList.description || ''}
                  onChange={(e) => setEditingList(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full h-20 resize-none"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Privacy</label>
                <div className="flex gap-2">
                  {[
                    { value: 'private', label: 'Private', icon: Lock },
                    { value: 'friends', label: 'Friends', icon: Users },
                    { value: 'public', label: 'Public', icon: Globe }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setEditingList(prev => ({ ...prev, privacy: value }))}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm flex items-center justify-center gap-2 transition-colors ${
                        editingList.privacy === value
                          ? 'border-dive-neon bg-dive-neon/20 text-white'
                          : 'border-dive-gray text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !editingList.title.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Save Changes'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyLists;

